// The compose setup gate — architecture/MAIL.md §3.2a.
//
// One read (`mail.setup get`) and four actions drive MailSetupPanel on all
// three of its mount points: the /mail empty state, the header drawer and the
// /settings Mail card. The panel NEVER decides a state itself — the server
// answers one of off | ready | reauth | connect | link | ask-admin and the UI
// renders exactly that.
//
// Two contracts this file is written around, both verified against the shipped
// server (server/src/domains/mail):
//   • `providers[].steps` is served ONLY when `viewerIsAdmin`. An empty list on
//     a member is NORMAL — never an error, never an empty-looking panel.
//   • `POST /setup/link` answers 501 today and `POST /setup/notify-admin`
//     answers 429 once a viewer has already asked inside the 24 h window. Both
//     are REAL UI STATES here: the message the server sends is what the person
//     reads, never a swallowed console warning.
//
// The provider OAuth start (`POST /accounts/connect/:provider`) is NOT
// registered on the server yet — it lands with MAIL-SERVER-GOOGLE-PROVIDER-1
// — so `startMailConnect` says so in the panel instead of firing a request
// that would 404 and read as a server fault.
import { getSDK } from '@symbo-ls/workspace-shared/functions/sdk.js'
import { mailSameWorkspaceWriter, mailWorkspaceId } from './sameWorkspaceWriter.js'

// Read the gate. Idempotent and shared: three surfaces mount the panel, so the
// in-flight flag plus a 15 s freshness window keep one mount's request from
// becoming three. `{ force: true }` re-reads after an action changed the facts.
export const loadMailSetup = async function (opts) {
  const root = (opts && opts.root) || (this && this.state && this.state.root)
  if (!root) return null
  if (root.mailSetupLoading) return root.mailSetup
  const fresh = root.mailSetup && Date.now() - (root.mailSetup.__fetchedAt || 0) < 15000
  if (fresh && !(opts && opts.force)) return root.mailSetup
  // Captured BEFORE the await — see functions/sameWorkspaceWriter.js.
  const write = mailSameWorkspaceWriter(root)
  const workspaceId = mailWorkspaceId(root)
  root.update({ mailSetupLoading: true, mailSetupError: null })
  try {
    const data = await getSDK().execute('mail.setup', 'get', workspaceId ? { workspaceId } : {})
    const payload = data && typeof data === 'object' ? data : null
    // ⚠️ THE LOADING LATCH IS NOT TENANT DATA. When the tenancy guard drops
    // the write below (the workspace changed — or RESOLVED, boot-window —
    // between capture and reply), the DATA must drop but the latch must
    // still clear: a dropped `mailSetupLoading: false` left the flag true
    // for the whole session, and every later gate read short-circuited on
    // it — openMailCompose then taught setup forever with a null state
    // (measured live 2026-09-05, headless probe on /mail direct load).
    // Cleared via a direct root.update on purpose: `false` is the key's
    // reset value, identical for every tenant.
    // ⚠️ `mailAccounts` IS NOT WRITTEN HERE, and that is the point. The gate
    // answers §3.2a SUMMARIES — { id, address, kind, provider, status,
    // canSend } — while `mail.accounts list` answers the FULL rows the
    // /settings card renders (sendAs, signature, settings, folders). Both
    // used to write the same key, both fire on the settings card's mount, and
    // `disconnectMailAccount` re-read them in an order that let the summary
    // land last: every surviving row silently lost its send-as picker and its
    // signature field. ONE key, ONE writer — `loadMailAccounts`. The
    // summaries stay where they are already read from, inside `mailSetup`.
    const landed = write({
      mailSetup: payload ? { ...payload, __fetchedAt: Date.now() } : null,
      mailSetupLoading: false
    })
    if (!landed) {
      root.update({ mailSetupLoading: false })
      // The drop above is usually the BOOT WINDOW: tenancy RESOLVED ('' → id)
      // between capture and reply, so the answer belonged to nobody and the
      // gate stayed null for the whole session — no surface retries it
      // (measured in the 2026-09-06 headless probe: direct /mail boot,
      // setup null / loading false / no error). ONE re-request against the
      // now-hydrated workspace; a second drop means a real mid-flight tenant
      // switch, and the next surface's own load owns that.
      if (!(opts && opts._hydrationRetry)) {
        return loadMailSetup.call(this, {
          ...(opts || {}),
          root,
          force: true,
          _hydrationRetry: true
        })
      }
    }
    return payload
  } catch (err) {
    const landed = write({
      mailSetupLoading: false,
      mailSetupError: {
        code: (err && err.cause && err.cause.code) || null,
        message: (err && err.message) || ''
      }
    })
    if (!landed) root.update({ mailSetupLoading: false })
    return null
  }
}

// [Link my mailbox] — asks the server to run the directory match for this
// viewer against the connected tenant. 501 today: the contract is frozen and
// its own message is what the person reads.
export const linkMailMailbox = async function () {
  const root = this && this.state && this.state.root
  if (!root || root.mailSetupBusy) return false
  const write = mailSameWorkspaceWriter(root)
  const workspaceId = mailWorkspaceId(root)
  root.update({ mailSetupBusy: 'link', mailSetupNotice: null })
  try {
    await getSDK().execute('mail.setup', 'link', workspaceId ? { workspaceId } : {})
    write({
      mailSetupBusy: null,
      mailSetupNotice: { tone: 'success', key: 'mail.setup.link.linked' }
    })
    await this.call('loadMailSetup', { force: true })
    return true
  } catch (err) {
    // The route's stable code + the values its sentence names ride on the
    // notice: a config error (the tenant's provider or the token vault the
    // platform runs without) then reads in the session language at render
    // time (functions/serverCopy.js); any other refusal keeps its sentence.
    const { code, params } = this.call('mailErrorOf', err) || {}
    write({
      mailSetupBusy: null,
      mailSetupNotice: {
        // 501 is the pending contract, not a fault — it reads as information.
        tone: err && err.status === 501 ? 'info' : 'error',
        key: err && err.status === 501 ? 'mail.setup.link.pending' : 'mail.setup.link.failed',
        message: (err && err.message) || '',
        code: code || '',
        params: params || null
      }
    })
    return false
  }
}

// [Notify an admin] — one Notification per org admin, once per viewer per day.
// 429 is the "already asked" state and carries the retry stamp; 409 means the
// viewer administers mail themselves and belongs on the admin page.
export const notifyMailAdmin = async function () {
  const root = this && this.state && this.state.root
  if (!root || root.mailSetupBusy) return false
  const write = mailSameWorkspaceWriter(root)
  const workspaceId = mailWorkspaceId(root)
  root.update({ mailSetupBusy: 'notifyAdmin', mailSetupNotice: null })
  try {
    await getSDK().execute('mail.setup', 'notifyAdmin', workspaceId ? { workspaceId } : {})
    write({
      mailSetupBusy: null,
      mailSetupNotice: { tone: 'success', key: 'mail.setup.askAdmin.notified' }
    })
    return true
  } catch (err) {
    const status = err && err.status
    write({
      mailSetupBusy: null,
      mailSetupNotice: {
        tone: status === 429 || status === 409 ? 'info' : 'error',
        key:
          status === 429
            ? 'mail.setup.askAdmin.already'
            : status === 409
              ? 'mail.setup.askAdmin.youAreAdmin'
              : 'mail.setup.askAdmin.failed',
        message: status === 429 ? '' : (err && err.message) || ''
      }
    })
    return false
  }
}

// [Continue with Google] / [Continue with Microsoft], and the [Reconnect] of a
// `reauth` account — both are the personal OAuth start. That route is not
// registered on the server yet (MAIL-SERVER-GOOGLE-PROVIDER-1), so the panel
// says exactly that rather than firing a request that would 404. When it
// lands, this body becomes the redirect and nothing above it changes.
export const startMailConnect = async function (provider) {
  const root = this && this.state && this.state.root
  if (!root) return false
  root.update({
    mailSetupBusy: null,
    mailSetupNotice: {
      tone: 'info',
      key: 'mail.setup.connect.pending',
      provider: provider || ''
    }
  })
  return false
}

// [Set up mail] — the admin destination. `/admin/mail` lands with
// MAIL-ADMIN-TENANT-POLICY-1; until it is registered on the router the button
// opens `/admin/integrations`, where the Google Workspace credential this gate
// reports on already lives. Reading the live page map (never a hardcoded
// "not yet") means the button follows the admin page the day it ships.
export const openMailAdminSetup = function () {
  const pages = (this && this.context && this.context.pages) || {}
  const route = pages['/admin/mail'] ? '/admin/mail' : '/admin/integrations'
  const root = this && this.state && this.state.root
  if (root && root._mailDrawerOpen) root.update({ _mailDrawerOpen: false })
  this.call('openAppPage', route)
  return route
}

// THE compose entry point, `openMailCompose` (§3.2a), lives in
// functions/compose.js with the rest of the draft lifecycle
// (MAIL-UI-COMPOSE-1) — this file keeps the gate READ + the setup actions.

// The copyable `code` line of a server-served setup step. The clipboard call
// lives HERE, never in a component (Rule 64 / FA503), and the outcome is a
// house toast — the same shape workspace/functions/shareLinks.js
// `copyShareLink` uses for the only other copy-to-clipboard button in the
// shell. A refusal is reported, never swallowed: the code stays on screen and
// is selectable, so the step is still followable.
export const copyMailSetupCode = async function (code) {
  const text = String(code || '')
  if (!text) return false
  try {
    if (typeof navigator === 'undefined' || !navigator.clipboard) throw new Error('no clipboard')
    await navigator.clipboard.writeText(text)
    this.call('openNotification', {
      type: 'success',
      title: this.call('polyglot', 'mail.setup.step.copied')
    })
    return true
  } catch (err) {
    this.call('openNotification', {
      type: 'error',
      title: this.call('polyglot', 'mail.setup.step.copyFailed')
    })
    return false
  }
}
